if (!customElements.get('cart-note')) {
  /**
   * CartNote Custom Web Component
   *
   * Manages gift message functionality in the shopping cart, providing interfaces for:
   * - Adding gift messages with recipient, sender, and message content
   * - Choosing between message cards and blank cards
   * - Form validation and character/line break limits
   * - State management between default, edit, and completed views
   * - Integration with Shopify cart attributes
   *
   * @class CartNote
   * @extends HTMLElement
   */
  class CartNote extends HTMLElement {
    /**
     * Creates an instance of CartNote
     * Initializes component state, storage keys, and default message data
     *
     * @constructor
     */
    constructor() {
      super();

      /**
       * Component view states
       * @type {Object}
       * @property {boolean} DEFAULT_VIEW - Initial state showing "add gift message" option
       * @property {boolean} EDIT_VIEW - Form state for editing gift message details
       * @property {boolean} GIFT_MESSAGE_ADDED_VIEW - Confirmation state showing message added
       */
      this.state = {
        DEFAULT_VIEW: true,
        EDIT_VIEW: false,
        GIFT_MESSAGE_ADDED_VIEW: false,
      };

      /**
       * Session storage keys for persisting gift message data
       * @type {Object}
       * @property {string} to - Storage key for recipient name
       * @property {string} from - Storage key for sender name
       * @property {string} message - Storage key for gift message content
       * @property {string} cardType - Storage key for selected card type
       */
      this.storage = {
        to: 'greyson_cart_note_to',
        from: 'greyson_cart_note_from',
        message: 'greyson_cart_note_message',
        cardType: 'greyson_cart_note_card_type',
      }

      /**
       * Gift message recipient name
       * @type {string}
       */
      this.messageTo = '';

      /**
       * Gift message sender name
       * @type {string}
       */
      this.messageFrom = '';

      /**
       * Gift message content
       * @type {string}
       */
      this.messageMessage = '';

      /**
       * Selected card type for the gift message
       * @type {string}
       * @default 'message-card'
       */
      this.selectedCardType = 'message-card'; // Default to message card
    }

    /**
     * Web Component lifecycle method - called when element is added to DOM
     * Triggers component initialization
     */
    connectedCallback() {
      this.init();
    }

    /**
     * Initializes the component by setting up elements, data, and initial state
     */
    init = () => {
      this.initElements();
      this.initMessageData();
      this.initState();
    }

    /**
     * Initializes DOM element references and clones template content
     * Sets up references to cart drawer, form, and template elements
     */
    initElements = () => {
      this.cartDrawer = this.closest('cart-drawer');
      this.cartForm = this.cartDrawer?.querySelector('form[name="cart"]');

      const getTemplate = (id) => this.querySelector(`#${id}`)?.content.firstElementChild.cloneNode(true);
      this.sendingAGift = getTemplate('sending-a-gift-template');
      this.giftMessageAdded = getTemplate('gift-message-added-template');
      this.editMessage = getTemplate('edit-message-template');
    }

    /**
     * Checks if minimum requirements for a gift message are met
     * For blank cards, no content is required. For message cards, at least one field must have content.
     *
     * @returns {boolean} True if requirements are met, false otherwise
     */
    areMessageRequirementsMet = () => {
      return this.selectedCardType === 'blank-card' || [this.messageTo, this.messageFrom, this.messageMessage].some(msg => msg !== '');
    }

    /**
     * Populates form fields with stored message data from session storage
     * Also handles radio button state and updates character/line break counters
     */
    insertMessageData = () => {
      const updateElementValue = (name, storageKey) => {
        const element = this.querySelector(`[name="cart-note-${name}"]`);
        const storedValue = sessionStorage.getItem(storageKey);
        if (element && storedValue) {
          element.value = storedValue;
        }
      };

      updateElementValue('to', this.storage.to);
      updateElementValue('from', this.storage.from);
      updateElementValue('message', this.storage.message);

      // Set the radio button state
      const storedCardType = sessionStorage.getItem(this.storage.cardType);
      if (storedCardType) {
        this.selectedCardType = storedCardType;
        const radioInput = this.querySelector(`input[name="cart-note-card-type"][value="${storedCardType}"]`);
        if (radioInput) {
          radioInput.checked = true;
        }
      }

      // Update limited values for message input based on current content
      const messageNoteInput = this.querySelector('.cart-note__input[name="cart-note-message"]');
      this.updateMaxCharacterCount(messageNoteInput);
      this.limitLineBreaks(messageNoteInput);
    }

    /**
     * Updates component properties and session storage with current form values
     * Syncs form data to component state and persistent storage
     */
    updateMessageData = () => {
      const updateStorageItem = (name, storageKey) => {
        const element = this.querySelector(`[name="cart-note-${name}"]`);
        if (element?.value) {
          this[`message${name.charAt(0).toUpperCase() + name.slice(1)}`] = element.value;
          sessionStorage.setItem(storageKey, element.value);
        } else {
          this[`message${name.charAt(0).toUpperCase() + name.slice(1)}`] = '';
          sessionStorage.removeItem(storageKey);
          if (element) element.value = '';
        }
      };

      updateStorageItem('to', this.storage.to);
      updateStorageItem('from', this.storage.from);
      updateStorageItem('message', this.storage.message);

      // Update the radio button state
      const selectedRadio = this.querySelector('input[name="cart-note-card-type"]:checked');
      if (selectedRadio) {
        this.selectedCardType = selectedRadio.value;
        sessionStorage.setItem(this.storage.cardType, this.selectedCardType);
      }
    }

    /**
     * Initializes message data properties from session storage
     * Loads previously saved gift message data if available
     */
    initMessageData = () => {
      ['To', 'From', 'Message'].forEach(field => {
        const storageKey = `greyson_cart_note_${field.toLowerCase()}`;
        this[`message${field}`] = sessionStorage.getItem(storageKey) || '';
      });
    }

    /**
     * Sets up event listeners based on current component state
     * Delegates to specific listener initialization methods per state
     */
    initEventListeners = () => {
      if (this.state.DEFAULT_VIEW) {
        this.querySelector('.cart-note__add-message')?.addEventListener('click', () => this.transitionState({ EDIT_VIEW: true }));
      } else if (this.state.GIFT_MESSAGE_ADDED_VIEW) {
        this.querySelector('.cart-note__edit-message')?.addEventListener('click', () => this.transitionState({ EDIT_VIEW: true }));
      } else if (this.state.EDIT_VIEW) {
        this.initEditViewListeners();
      }
    }

    /**
     * Initializes event listeners specific to the edit view
     * Handles form submission, input validation, character limits, and navigation
     */
    initEditViewListeners = () => {
      // Form elements
      const messageForm = this.querySelector('.cart-note__form');
      const messageFormInputs = messageForm?.querySelectorAll('.cart-note__input');
      const messageNoteInput = messageForm?.querySelector('.cart-note__input[name="cart-note-message"]');

      // Add input event listeners to all form input elements
      messageFormInputs?.forEach(formInput => {
        formInput.addEventListener('input', () => {
          this.removeInvalidCharacters(messageForm, formInput);
          // Special adjustments for message input
          if (formInput === messageNoteInput) {
            this.limitLineBreaks(formInput);
            this.updateMaxCharacterCount(formInput);
          }
        });
      });

      messageForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.updateMessageData();

        // Get the selected card type
        const isBlankCard = this.selectedCardType === 'blank-card';

        // Update cart attributes with the gift message
        const attributes = {
          'gift_message_to': isBlankCard ? '' : this.messageTo,
          'gift_message_from': isBlankCard ? '' : this.messageFrom,
          'gift_message': isBlankCard ? '' : this.messageMessage,
          'gift_message_blank_card': isBlankCard ? true : ''
        };

        fetch(`${window.theme.routes.cart_update_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ attributes })
        })
        .then(response => response.json())
        .then(() => {
          this.transitionState({ [this.areMessageRequirementsMet() ? 'GIFT_MESSAGE_ADDED_VIEW' : 'DEFAULT_VIEW']: true });
        })
        .catch(error => {
          console.error('Error updating cart attributes:', error);
        });
      });

      this.querySelector('.cart-note__back')?.addEventListener('click', () => {
        this.transitionState({ [this.areMessageRequirementsMet() ? 'GIFT_MESSAGE_ADDED_VIEW' : 'DEFAULT_VIEW']: true });
      });
    }

    /**
     * Validates and removes invalid characters from form inputs
     * Shows/hides error messages based on validation results
     *
     * @param {HTMLFormElement} messageForm - The form containing allowed characters data
     * @param {HTMLInputElement} formInput - The input element to validate
     */
    removeInvalidCharacters = (messageForm, formInput) => {
      const inputValue = formInput.value;

      const escapeRegExp = (string) => {
        // First unescape any escaped characters, then escape all characters properly
        // Handle hyphens separately to avoid range interpretation
        return string.replace(/\\([.*+?^${}()|[\]\\])/g, '$1')
          .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
          .replace(/(?<!\\)-/g, '\\-');
      };

      // Get the allowed characters from the data attribute and escape any special characters
      const allowedChars = messageForm?.getAttribute('data-allowed-characters');
      const escapedAllowedChars = escapeRegExp(allowedChars);
      // Always allow spaces in addition to the allowed characters
      const validCharsRegex = new RegExp(`^[${escapedAllowedChars}\\s]*$`);

      // Remove any characters not allowed by the regex
      const hasInvalidCharacters = !validCharsRegex.test(inputValue);
      if (hasInvalidCharacters) {
        formInput.value = inputValue.replace(new RegExp(`[^${escapedAllowedChars}\\s]`, 'g'), '');
      }

      // Update visibility of the prohibited character error message
      const prohibitedCharacterError = this.querySelector('.cart-note__alerts-error');
      prohibitedCharacterError.toggleAttribute('hidden', !hasInvalidCharacters);
    }

    /**
     * Counts the number of line breaks in a given text string
     *
     * @param {string} text - Text to analyze for line breaks
     * @returns {number} Number of line breaks found
     */
    countLineBreaks = (text) => (text.match(/\n/g) || []).length;

    /**
     * Enforces line break limits on form inputs and updates counter display
     * Removes excess line breaks and updates the remaining line break count
     *
     * @param {HTMLInputElement} formInput - Input element with line break limit data attribute
     */
    limitLineBreaks = (formInput) => {
      const lineBreakLimit = parseInt(formInput?.dataset.lineBreakLimit);
      if (Number.isNaN(lineBreakLimit)) return;

      const lineBreaks = this.countLineBreaks(formInput.value);

      // Check if we've exceeded the line break limit
      if (lineBreaks > lineBreakLimit) {
        formInput.value = formInput.value.replace(/\n/g, (match, index) =>
          this.countLineBreaks(formInput.value.slice(0, index)) < lineBreakLimit ? match : ''
        );
      }

      // Update the line break limit counter
      const lineBreakLimitCount = this.querySelector('.cart-note__alerts-limit-count');
      if (lineBreakLimitCount) {
        lineBreakLimitCount.textContent = `${lineBreakLimit - this.countLineBreaks(formInput.value)}`;
      }
    }

    /**
     * Updates the character count display for form inputs
     * Shows current character count vs maximum allowed characters
     *
     * @param {HTMLInputElement} formInput - Input element with maxlength attribute
     */
    updateMaxCharacterCount = (formInput) => {
      // Update the character count based on existing message
      const messageCount = this.querySelector('.cart-note__input-count');

      if (formInput && messageCount) {
        const maxLength = formInput.getAttribute('maxlength');
        const currentLength = formInput.value.length;
        messageCount.textContent = `${currentLength}/${maxLength}`;
      }
    }

    /**
     * Initializes the component's initial state based on existing message data
     * Determines whether to show default view or gift message added view
     */
    initState = () => {
      this.transitionState({ [this.areMessageRequirementsMet() ? 'GIFT_MESSAGE_ADDED_VIEW' : 'DEFAULT_VIEW']: true });
    }

    /**
     * Transitions the component to a new state
     * Updates state object and triggers UI re-render
     *
     * @param {Object} newState - Object with state key set to true for desired state
     */
    transitionState = (newState) => {
      this.state = Object.fromEntries(Object.keys(this.state).map(key => [key, newState[key] || false]));
      this.setState();
    }

    /**
     * Renders the component UI based on current state
     * Manages DOM content, cart drawer visibility, and event listener initialization
     */
    setState = () => {
      const currentState = Object.keys(this.state).find(key => this.state[key]) || 'DEFAULT_VIEW';
      this.innerHTML = '';

      if (currentState === 'DEFAULT_VIEW') {
        this.restoreCartDrawerElements();
        this.innerHTML = this.sendingAGift.innerHTML;
      } else if (currentState === 'GIFT_MESSAGE_ADDED_VIEW') {
        this.restoreCartDrawerElements();
        this.innerHTML = this.giftMessageAdded.innerHTML;
      } else if (currentState === 'EDIT_VIEW') {
        this.innerHTML = this.editMessage.innerHTML;
        this.hideCartDrawerElements();
        this.insertMessageData();
      }

      this.initEventListeners();
    }

    /**
     * Restores visibility of cart drawer elements
     * Shows cart header, footer, and other cart content
     */
    restoreCartDrawerElements = () => {
      this.toggleCartDrawerElements(false);
    }

    /**
     * Hides cart drawer elements to focus on gift message editing
     * Hides cart header, footer, and other cart content
     */
    hideCartDrawerElements = () => {
      this.toggleCartDrawerElements(true);
    }

    /**
     * Toggles visibility of cart drawer elements
     * Manages the expanded state and visibility of various cart UI components
     *
     * @param {boolean} hide - Whether to hide (true) or show (false) cart elements
     */
    toggleCartDrawerElements = (hide) => {
      if (!this.cartDrawer) return;
      this.classList.toggle('expand', hide);
      ['.cart-drawer__head', '.cart__cart-announcement', '.cart-gwp-selector', '.cart-drawer__foot']
        .forEach(selector => this.cartDrawer.querySelector(selector)?.classList.toggle('hidden', hide));
      this.cartDrawer.querySelectorAll('.cart-drawer__body > *:not(cart-note)')
        .forEach(el => el.classList.toggle('hidden', hide));
    }
  }

  customElements.define('cart-note', CartNote);
}
